import React from 'react'
import { initializeApollo } from '@gql/client'
import {
  AllCaratteristicheDocument,
  AreaDownloadAziendaDocument,
  AreaDownloadCataloghiCollezioniDocument,
  AreaDownloadCataloghiGeneraliDocument,
  PaginaDocument,
} from '@gql/graphql'
import { NextWrapper } from 'data-fetching-wrapper'
import { AreaDownloadTemplate } from '@components/templates'
import useTrans from '@hooks/useTrans'

const DownloadAreaPage = ({
  pagina,
  cataloghiCollezioni,
  cataloghiGenerali,
  downloadAzienda,
  certificazioni,
}) => {
  const t = useTrans()
  const brands = (cataloghiCollezioni || []).concat(cataloghiGenerali || []).reduce(
    (res, x) => ({
      ...res,
      [x.brand.pk]: x.brand.title,
    }),
    {}
  )

  return (
    <AreaDownloadTemplate
      pagina={pagina}
      items={[
        {
          key: 'cataloghi',
          label: t('Cataloghi'),
          type: 'brand' as 'brand' | 'default',
          children: Object.keys(brands)?.length
            ? Object.keys(brands)
                .map((b) => ({
                  pk: b,
                  name: brands[b],
                  children: [
                    {
                      pk: 'collezioni',
                      name: t('Collezioni'),
                      children: (cataloghiCollezioni || [])
                        .filter((c) => c.brand?.pk?.toString() === b)
                        .map((x) => ({
                          pk: x.id,
                          name: x.nomeWeb,
                          url: x.url,
                        })),
                    },
                    {
                      key: 'generale',
                      name: t('Catalogo generale'),
                      children: (cataloghiGenerali || [])
                        .filter((c) => c.brand?.pk?.toString() === b)
                        .map((x) => ({
                          pk: x.id,
                          name: x.nome,
                          url: x.url,
                        })),
                    },
                  ].filter((x) => x.children?.length),
                }))
                .filter((x) => x.children?.length)
            : [],
        },
        {
          key: 'certificazioni',
          label: t('Certificazioni'),
          type: 'default' as 'brand' | 'default',
          children: (certificazioni || []).map((c) => ({
            pk: c.id,
            name: c.title,
            children: c.children?.map((x) => ({
              pk: x.pk,
              name: x.title,
              url: x.url,
            })) || [
              {
                pk: c.id,
                name: c.title,
                url: c.url,
              },
            ],
          })),
        },
        {
          key: 'azienda',
          label: t('Azienda'),
          type: 'default' as 'brand' | 'default',
          children: [
            {
              pk: 'documenti',
              name: t('Documenti'),
              children: (downloadAzienda || []).map((x) => ({
                pk: x.id,
                name: x.nome,
                url: x.url,
              })),
            },
          ].filter((x) => x.children?.length),
        },
      ].filter((x) => x.children?.length)}
    />
  )
}

export const getStaticProps = NextWrapper.getStaticProps(
  async ({ locale, preview, previewData }) => {
    const client = initializeApollo(locale)

    const headers = preview && previewData?.headers ? { cookie: previewData.headers } : {}

    const {
      data: { pagina },
    } = await client.query({
      query: PaginaDocument,
      variables: { chiave: 'downloadarea', preview },
      context: {
        headers,
      },
    })

    if (!pagina) {
      return {
        notFound: true,
      }
    }

    const {
      data: { areaDownloadCataloghiCollezioni: cataloghiCollezioni },
    } = await client.query({
      query: AreaDownloadCataloghiCollezioniDocument,
    })

    const {
      data: { areaDownloadCataloghiGenerali: cataloghiGenerali },
    } = await client.query({
      query: AreaDownloadCataloghiGeneraliDocument,
    })

    const {
      data: { areaDownloadAzienda: downloadAzienda },
    } = await client.query({
      query: AreaDownloadAziendaDocument,
    })

    const {
      data: { allCaratteristiche: certificazioni },
    } = await client.query({
      query: AllCaratteristicheDocument,
      context: {
        headers,
      },
    })

    return {
      props: { pagina, cataloghiCollezioni, cataloghiGenerali, downloadAzienda, certificazioni },
      revalidate: 60 * 20,
    }
  },
  '/downloadarea'
)

export default DownloadAreaPage
